import React, { useState, useCallback, useMemo } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import Checkbox from "aldoo-ra/Checkbox"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { showNotification } from "aldoo-ra/Notification"
import { DeactivateDevice, GetLicenseDevices, DeactivateDevices } from "../../api"
import { 
  TrashIcon,
} from "@heroicons/react/24/outline"
import { getPlatformIcon } from "../../PlatformIcons"
import Tooltip from "aldoo-ra/Tooltip"
import { renderPlatformIcon, getPlatformDisplayName } from "aldoo-ra/Admin/utils/platformIconUtils"

const DevicesTab = ({ license, refreshKey, setRefreshKey }) => {
  const [selectedDevices, setSelectedDevices] = useState([])

  // Create stable API method reference and request fields
  const requestFields = useMemo(() => {
    return {
      licenseId: license._id
    }
  }, [license._id])

  // Create stable callback for data loading
  const handleDataLoaded = useCallback((data) => {
    return data
  }, [])

  // Handle device deactivation
  const handleDeactivateDevice = (deviceId) => {
    // Find device name for better UX
    const device = license.devices.find(d => d.deviceId === deviceId || d.deviceID === deviceId)
    const deviceName = device?.name || device?.deviceName || deviceId

    showPromptDialog({
      title: "Deactivate Device",
      message: `Are you sure you want to deactivate "${deviceName}"? This will free up a seat for other users.`,
      confirmText: "Deactivate",
      confirmVariant: "warning",
      onConfirm: async () => {
        try {
          const response = await DeactivateDevice({
            licenseId: license._id,
            deviceId: deviceId
          })

          if (response.result) {
            showNotification({
              message: `Device "${deviceName}" deactivated successfully`,
              className: "bg-admin_success text-white",
            })
            // Refresh the license data to show updated device list
            setRefreshKey(prev => prev + 1)
          } else {
            showNotification({
              message: response.message || "Failed to deactivate device",
              className: "bg-admin_error text-white",
            })
          }
        } catch (error) {
          console.error('Device deactivation error:', error)
          showNotification({
            message: "Error deactivating device",
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Handle bulk device removal
  const handleBulkRemoveDevices = () => {
    if (selectedDevices.length === 0) return

    const deviceCount = selectedDevices.length
    const deviceText = deviceCount === 1 ? 'device' : 'devices'

    showPromptDialog({
      title: `Remove ${deviceCount} ${deviceText}`,
      message: `Are you sure you want to deactivate ${deviceCount} ${deviceText}? This will free up ${deviceCount} seat${deviceCount === 1 ? '' : 's'} for other users.`,
      confirmText: `Remove ${deviceCount} ${deviceText}`,
      confirmVariant: "warning",
      onConfirm: async () => {
        try {
          // Use bulk device removal API
          const response = await DeactivateDevices({
            licenseId: license._id,
            deviceIds: selectedDevices,
          })

          if (response.result && response.data) {
            const { successCount, errorCount, errors, message } = response.data

            // Show success notification with backend message
            if (successCount > 0) {
              showNotification({
                message: message || `${successCount} ${successCount === 1 ? 'device' : 'devices'} deactivated successfully`,
                className: "bg-admin_success text-white",
              })
            }

            // Log specific errors for debugging
            if (errors && errors.length > 0) {
              console.error('Device removal errors:', errors)
            }

            // Show error notification if all failed
            if (errorCount > 0 && successCount === 0) {
              showNotification({
                message: `Failed to deactivate all ${errorCount} ${errorCount === 1 ? 'device' : 'devices'}`,
                className: "bg-admin_error text-white",
              })
            }

            // Clear selection and refresh data
            setSelectedDevices([])
            setRefreshKey(prev => prev + 1)
          } else {
            // Handle API error response
            showNotification({
              message: response.message || response.error || "Failed to deactivate devices",
              className: "bg-admin_error text-white",
            })
          }
        } catch (error) {
          console.error('Bulk device removal error:', error)
          showNotification({
            message: "Error during bulk device removal",
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Table footer content - action buttons
  const tableFooter = selectedDevices.length > 0 && (
    <div className="flex justify-end items-center mt-4 p-3 bg-admin_canvas rounded">
      <Typography 
        variant="admin_body" 
        className="mr-4"
      >
        {selectedDevices.length} device{selectedDevices.length === 1 ? "" : "s"} selected
      </Typography>

      <Button
        variant="admin_secondary"
        className="mr-3"
        onClick={() => setSelectedDevices([])}
      >
        Deselect
      </Button>

      <Button 
        variant="admin_error" 
        onClick={() => handleBulkRemoveDevices()}
      >
        <TrashIcon className="w-5 h-5 mr-2" />
        Remove
      </Button>
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Active Devices DataTable */}
      <div className="bg-admin_paper rounded-xl p-6">
        <DataTable
          localUserPreferencesKey="admin.licensing.viewer.devices"
          variant="admin_browser"
          apiMethod={GetLicenseDevices}
          requestFields={requestFields}
          refreshKey={refreshKey}
          searchField="name model platform"
          itemsPerPage={10}
          tableFooter={tableFooter}
          headerControls={
            <div className="absolute top-2 left-0">
              <Typography 
                variant="admin_h4"
                className="!text-xl"
              >
                Active Devices ({license.devices?.length || 0}/{license.seats}) <span className="text-admin_text_secondary">{Math.round(((license.devices?.length || 0) / license.seats) * 100)}%</span>
              </Typography>
            </div>
          }
          styleOverrides={{
            table: "w-full table-fixed",
            getRowStyle: (index, item) => {
              const isSelected = selectedDevices.includes(item._id || item.deviceId)
              let style = index % 2 === 0 ? "bg-admin_paper" : "bg-admin_canvas"
              
              if (isSelected) {
                style = "bg-admin_primary/10 border-l-4 border-admin_primary"
              }
              
              style += " hover:bg-admin_neutral_1 transition-colors duration-200 cursor-pointer"
              return style
            }
          }}
          renders={[
            {
              id: "select",
              headerClassName: "w-8",
              header: (_, items) => (
                <Checkbox
                  variant="admin_compact"
                  isOn={items.length > 0 && selectedDevices.length === items.length}
                  onChange={(isSelected) => {
                    if (isSelected) {
                      setSelectedDevices(items.map((item) => item._id || item.deviceId))
                    } else {
                      setSelectedDevices([])
                    }
                  }}
                />
              ),
              render: (_, item) => (
                <Checkbox
                  variant="admin_compact"
                  isOn={selectedDevices.includes(item._id || item.deviceId)}
                  onChange={(isSelected) => {
                    const deviceId = item._id || item.deviceId
                    if (isSelected) {
                      setSelectedDevices((prev) => [...prev, deviceId])
                    } else {
                      setSelectedDevices((prev) => prev.filter((id) => id !== deviceId))
                    }
                  }}
                />
              ),
            },
            {
              id: "device",
              title: "Device",
              headerClassName: "w-[200px]",
              render: (_, device) => (
                <div className="flex items-center space-x-3">
                  {getPlatformIcon(device.platform, "w-8 h-8 text-admin_text_secondary")}
                  <div>
                    <Typography
                      variant="admin_body"
                      className="font-medium my-0"
                    >
                      {device.name || device.deviceName || "Unknown Device"}
                    </Typography>

                    {device.model && (
                      <Typography
                        variant="admin_txt2" className="text-admin_text_secondary"
                      >
                        {device.model}
                      </Typography>
                    )}
                  </div>
                </div>
              ),
            },
            {
              id: "platform",
              title: "Platform",
              headerClassName: "w-[120px] text-center",
              render: (_, device) => (
                <div className="flex items-center justify-center">
                  {device.platform && device.platform !== "Unknown" ? (
                    <Tooltip content={getPlatformDisplayName(device.platform)}>
                      <div className="flex items-center justify-center w-6 h-6">
                        {renderPlatformIcon(device.platform, "w-full h-full text-admin_text_secondary")}
                      </div>
                    </Tooltip>
                  ) : (
                    <Tooltip content="Unknown Platform">
                      <div className="flex items-center justify-center w-6 h-6">
                        {renderPlatformIcon("unknown", "w-full h-full text-admin_text_secondary")}
                      </div>
                    </Tooltip>
                  )}
                </div>
              ),
            },
            {
              id: "status",
              title: "Status",
              headerClassName: "w-[80px]",
              render: (_, device) => (
                <div className="flex items-center space-x-2">
                  <div 
                    className={`w-2 h-2 rounded-full ${
                      device.online !== undefined 
                        ? (device.online ? "bg-admin_success" : "bg-admin_error") 
                        : "bg-admin_success"
                    }`}
                  />

                  <Typography variant="admin_txt1_dim">
                    {device.online !== undefined ? (device.online ? "Online" : "Offline") : "Active"}
                  </Typography>
                </div>
              ),
            },
            {
              id: "lastSeen",
              title: "Last Seen",
              headerClassName: "w-[80px]",
              render: (_, device) => (
                <Typography variant="admin_txt1_dim">
                  {new Date(device.lastActive || device.lastPing || device.lastLogin || device.created).toLocaleDateString()}
                </Typography>
              ),
            },
            {
              id: "actions",
              title: "Actions",
              headerClassName: "w-[100px]",
              render: (_, device) => (
                <Button
                  variant="admin_secondary"
                  onClick={() => handleDeactivateDevice(device.deviceId || device.deviceID)}
                >
                  <TrashIcon className="w-4 h-4 mr-2" />
                  Deactivate
                </Button>
              ),
            },
          ]}
          noData={
            <div className="text-center py-12">
              {getPlatformIcon(null, "w-16 h-16 text-admin_text_secondary mx-auto mb-4")}

              <Typography 
                variant="admin_h4" 
                className="text-admin_text_secondary mb-2"
              >
                No devices activated yet
              </Typography>

              <Typography 
                variant="admin_txt1" 
                className="text-admin_text_secondary"
              >
                Users can activate up to {license.seats} device{license.seats > 1 ? "s" : ""} with this license
              </Typography>
            </div>
          }
          onDataLoaded={handleDataLoaded}
        />
      </div>

      {/* Device Management Info */}
      <div className="bg-admin_paper rounded-xl p-6">
        <Typography 
          variant="admin_h4" 
          className="mb-4"
        >
          Device Management
        </Typography>

        <ul className="space-y-3 list-disc text-admin_text pl-5">
          <li>
            <strong>Seat Limit:</strong> This license supports up to {license.seats} active device{license.seats > 1 ? "s" : ""}
          </li>

          <li>
            <strong>Device Registration:</strong> When users activate the license, their device is automatically registered
          </li>

          <li>
            <strong>Deactivation:</strong> Removing a device frees up a seat for other users
          </li>

          <li>
            <strong>Web Clients:</strong> Browser-based activations don't count towards seat limits
          </li>
        </ul>
      </div>
    </div>
  )
}

export default DevicesTab